import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Inbox, Users, Mic, ArrowRight } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[var(--bg-card)] border-glow rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <span className="text-sm text-[var(--text-secondary)]">{label}</span>
    </div>
    <p className="text-3xl font-bold text-white">{value}</p>
  </div>
);

const statusLabels = { idea: "Idea", researching: "Researching", ai_draft: "AI Draft", review: "Review", approved: "Approved", scheduled: "Scheduled", published: "Published" };
const statusColors = { idea: "bg-slate-500/20 text-slate-300", researching: "bg-blue-500/20 text-blue-300", ai_draft: "bg-violet-500/20 text-violet-300", review: "bg-amber-500/20 text-amber-300", approved: "bg-emerald-500/20 text-emerald-300", scheduled: "bg-cyan-500/20 text-cyan-300", published: "bg-green-500/20 text-green-300" };

const AdminDashboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    adminApi("/admin/dashboard").then(setData).catch((e) => toast.error(e.message));
  }, []);

  if (!data) return <div className="text-[var(--text-secondary)]">Loading dashboard...</div>;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-1" data-testid="dashboard-heading">Intelligence Desk</h1>
        <p className="text-sm text-[var(--text-secondary)]">Content overview and editorial workflow</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FileText} label="Published" value={data.published} color="bg-emerald-500/20 text-emerald-400" />
        <StatCard icon={FileText} label="Drafts" value={data.drafts} color="bg-amber-500/20 text-amber-400" />
        <StatCard icon={Inbox} label="New Signals" value={data.signals_new} color="bg-violet-500/20 text-violet-400" />
        <StatCard icon={Users} label="Subscribers" value={data.subscribers} color="bg-blue-500/20 text-blue-400" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Articles */}
        <div className="bg-[var(--bg-card)] border-glow rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent Articles</h2>
            <Link to="/admin/articles" className="text-xs text-[var(--accent-violet-light)] flex items-center gap-1">View All <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {data.recent_articles.length === 0 ? (
            <p className="text-sm text-[var(--text-secondary)]">No articles yet. Create your first one!</p>
          ) : (
            <div className="space-y-3">
              {data.recent_articles.map((a) => (
                <Link key={a._id} to={`/admin/articles/${a._id}/edit`} className="flex items-center justify-between py-2 group" data-testid={`recent-article-${a._id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate group-hover:text-[var(--accent-violet-light)] transition-colors">{a.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[a.status] || "bg-slate-500/20 text-slate-300"}`}>
                        {statusLabels[a.status] || a.status}
                      </span>
                      {a.category && <span className="text-[10px] text-[var(--text-secondary)]">{a.category}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-[var(--bg-card)] border-glow rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link to="/admin/articles/new" className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--accent-violet)]/10 transition-all group" data-testid="quick-new-article">
              <FileText className="w-5 h-5 text-[var(--accent-violet-light)]" />
              <div>
                <p className="text-sm text-white font-medium group-hover:text-[var(--accent-violet-light)]">New Future Signal</p>
                <p className="text-xs text-[var(--text-secondary)]">Create a new article or signal</p>
              </div>
            </Link>
            <Link to="/admin/signals" className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--accent-violet)]/10 transition-all group" data-testid="quick-signals">
              <Inbox className="w-5 h-5 text-[var(--accent-violet-light)]" />
              <div>
                <p className="text-sm text-white font-medium group-hover:text-[var(--accent-violet-light)]">Signals Inbox ({data.signals_new})</p>
                <p className="text-xs text-[var(--text-secondary)]">Review automated signal drafts</p>
              </div>
            </Link>
            <Link to="/admin/inquiries" className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--accent-violet)]/10 transition-all group" data-testid="quick-inquiries">
              <Mic className="w-5 h-5 text-[var(--accent-violet-light)]" />
              <div>
                <p className="text-sm text-white font-medium group-hover:text-[var(--accent-violet-light)]">Speaking Inquiries ({data.speaking_inquiries})</p>
                <p className="text-xs text-[var(--text-secondary)]">Review booking requests</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
